import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import LoadingFallback from '../components/LoadingFallback';
import { api, apiErrorMessage } from '../api/client';
import { useFeedback } from '../contexts/FeedbackContext';
import type { Meet, Student } from '../api/types';

export default function StudentsPage() {
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const studentsQuery = useQuery<Student[]>({
    queryKey: ['students', search],
    queryFn: async () => (await api.get('/students', { params: { q: search || undefined } })).data,
  });
  const meetsQuery = useQuery<Meet[]>({
    queryKey: ['meets'],
    queryFn: async () => (await api.get('/meets')).data,
  });

  const createMutation = useMutation({
    mutationFn: async (payload: any) => (await api.post('/students', payload)).data,
    onSuccess: () => {
      toast({ message: 'Student added', severity: 'success' });
      setCreateOpen(false);
      qc.invalidateQueries({ queryKey: ['students'] });
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  return (
    <Box>
      <PageHeader
        title="Athletes"
        subtitle="The students or participants registered with your organization."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            aria-label="Add student"
          >
            Add student
          </Button>
        }
      />

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: 2 }}>
          <TextField
            placeholder="Search by name or bib"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            inputProps={{ 'aria-label': 'Search students' }}
          />
        </CardContent>
      </Card>

      {studentsQuery.data && studentsQuery.data.length > 0 && (
        <RosterNextStepCard
          students={studentsQuery.data}
          meets={meetsQuery.data ?? []}
          onAddStudent={() => setCreateOpen(true)}
        />
      )}

      <Card>
        {studentsQuery.isLoading ? (
          <LoadingFallback />
        ) : (studentsQuery.data ?? []).length === 0 ? (
          <Box sx={{ p: 3 }}>
            <EmptyState
              title="No athletes yet"
              description="Add your first athlete to start registering them in events."
              action={
                <Button variant="contained" onClick={() => setCreateOpen(true)} aria-label="Add first student">
                  Add student
                </Button>
              }
            />
          </Box>
        ) : (
          <Table size="small" aria-label="Athletes">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Grade</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Bib</TableCell>
                <TableCell>Coaches & admins</TableCell>
                <TableCell>Parents</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(studentsQuery.data ?? []).map((s) => (
                <TableRow
                  key={s.id}
                  hover
                  component={RouterLink}
                  to={`/students/${s.id}`}
                  sx={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                  aria-label={`Open ${s.full_name}`}
                >
                  <TableCell><strong>{s.full_name}</strong></TableCell>
                  <TableCell>{s.grade || '—'}</TableCell>
                  <TableCell>{s.gender || '—'}</TableCell>
                  <TableCell>{s.bib_number || '—'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {s.coaches.length === 0 && <Chip size="small" label="None" variant="outlined" />}
                      {s.coaches.slice(0, 2).map((c) => (
                        <Chip key={c.id} size="small" label={`${c.full_name}${c.role === 'admin' ? ' (admin)' : ''}`} />
                      ))}
                      {s.coaches.length > 2 && <Chip size="small" label={`+${s.coaches.length - 2}`} />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {s.parents.length === 0 && <Chip size="small" label="None" variant="outlined" />}
                      {s.parents.slice(0, 2).map((p) => (
                        <Chip key={p.id} size="small" label={p.full_name} />
                      ))}
                      {s.parents.length > 2 && <Chip size="small" label={`+${s.parents.length - 2}`} />}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <CreateStudentDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(payload) => createMutation.mutate(payload)}
        busy={createMutation.isPending}
      />
    </Box>
  );
}

function CreateStudentDialog({
  open,
  onClose,
  onSubmit,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: any) => void;
  busy: boolean;
}) {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [grade, setGrade] = useState('');
  const [gender, setGender] = useState('');
  const [bib, setBib] = useState('');
  const [dob, setDob] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFirst('');
      setLast('');
      setGrade('');
      setGender('');
      setBib('');
      setDob('');
      setParentEmail('');
      setError(null);
    }
  }, [open]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!first.trim() || !last.trim()) {
      setError('First and last name are required.');
      return;
    }
    onSubmit({
      first_name: first,
      last_name: last,
      grade: grade || undefined,
      gender: gender || undefined,
      bib_number: bib || undefined,
      dob: dob || undefined,
      parent_email: parentEmail || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add athlete</DialogTitle>
      <form onSubmit={submit} aria-label="Add athlete form">
        <DialogContent>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="First name" value={first} onChange={(e) => setFirst(e.target.value)} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Last name" value={last} onChange={(e) => setLast(e.target.value)} required />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Grade" value={grade} onChange={(e) => setGrade(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Gender" value={gender} onChange={(e) => setGender(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Bib #" value={bib} onChange={(e) => setBib(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date of birth"
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Parent email"
                  type="email"
                  value={parentEmail}
                  onChange={(e) => setParentEmail(e.target.value)}
                  helperText="Optional, but required before Baton can send this athlete's schedule to a parent."
                  inputProps={{ 'aria-label': 'Parent email' }}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} aria-label="Cancel">Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy} aria-label="Save athlete">
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function RosterNextStepCard({
  students,
  meets,
  onAddStudent,
}: {
  students: Student[];
  meets: Meet[];
  onAddStudent: () => void;
}) {
  const parentCoverage = students.filter((s) => s.parents.length > 0).length;
  const nextMeet = meets.find((m) => (m.event_count ?? 0) === 0 || (m.registration_count ?? 0) === 0) ?? meets[0];

  return (
    <Alert
      severity={parentCoverage === students.length && meets.length > 0 ? 'success' : 'info'}
      sx={{ mb: 2 }}
      action={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {parentCoverage < students.length && (
            <Button color="inherit" size="small" onClick={onAddStudent} aria-label="Add athlete with parent email">
              Add athlete + parent
            </Button>
          )}
          {meets.length === 0 ? (
            <Button component={RouterLink} to="/meets" color="inherit" size="small" aria-label="Create a meet">
              Create meet
            </Button>
          ) : (
            <Button component={RouterLink} to={`/meets/${nextMeet.id}/prepare`} color="inherit" size="small" aria-label="Prepare meet">
              Prepare meet
            </Button>
          )}
        </Stack>
      }
    >
      {parentCoverage < students.length
        ? `${parentCoverage}/${students.length} athletes have parent emails. Add parent emails before sending schedules.`
        : 'Roster and parent emails are ready. Prepare a meet to assign entries and send schedules.'}
    </Alert>
  );
}
